import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDashboardStatistics } from './useDashboardStatistics'

export interface TypeDistributionData {
  type: 'physical' | 'digital'
  name: string
  value: number
  percentage: number
  color: string
}

export interface UseTypeDistributionReturn {
  data: TypeDistributionData[]
  totalBooks: number
  isLoading: boolean
  error: Error | null
}

/**
 * Hook for type distribution data (physical vs digital)
 * Reuses data from useDashboardStatistics for maximum efficiency
 */
export function useTypeDistribution(): UseTypeDistributionReturn {
  const { t } = useTranslation()
  const { statistics, isLoading, error } = useDashboardStatistics()

  const data = useMemo((): TypeDistributionData[] => {
    if (!statistics || statistics.totalBooks === 0) {
      return []
    }

    return [
      {
        type: 'physical' as const,
        name: t('books.type.physical'),
        value: statistics.physicalBooks,
        percentage: statistics.physicalPercentage,
        color: '#F59E0B', // Amber for physical books (same as roulette colors)
      },
      {
        type: 'digital' as const,
        name: t('books.type.digital'),
        value: statistics.digitalBooks,
        percentage: statistics.digitalPercentage,
        color: '#8B5CF6', // Purple for digital books (same as roulette colors)
      },
    ].filter((item) => item.value > 0) // Remove empty categories
  }, [statistics, t])

  return {
    data,
    totalBooks: statistics.totalBooks,
    isLoading,
    error,
  }
}
